import { DISCORD_URL } from "@/config/social";
import { useTranslation } from "@repo/shared/i18n";
import { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";
import { useState } from "react";
import { useSelector } from "react-redux";

import { getUserSelector } from "../redux/user/userSlice";

export const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{
    error: Error | null;
    componentStack: string | null;
    eventId: string | null;
  }>({
    error: null,
    componentStack: null,
    eventId: null,
  });

  const { user } = useSelector(getUserSelector);
  const { t } = useTranslation();

  const copyErrorReport = async () => {
    try {
      // Get additional browser and environment info
      const getBrowserInfo = () => {
        const nav = navigator;
        return {
          browser: `${nav.appName} ${nav.appVersion}`,
          platform: nav.platform,
          language: nav.language,
          cookieEnabled: nav.cookieEnabled,
          onLine: nav.onLine,
          screenResolution: `${screen.width}x${screen.height}`,
          viewportSize: `${window.innerWidth}x${window.innerHeight}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
      };

      const browserInfo = getBrowserInfo();
      const isLoggedIn = !!user?.uuid;
      const subscription = user?.subscription || "unknown";
      const platform = user?.platform || "unknown";

      const errorInfo = {
        timestamp: new Date().toISOString(),
        url: window.location.href,
        error: errorDetails.error?.message || "Unknown error occurred",
        sentryEventId: errorDetails.eventId || "No Sentry event ID available",
        errorBoundary: "React Error Boundary caught this error",

        // User information
        userLoggedIn: isLoggedIn,
        userId: user?.uuid || "Not logged in",
        userSubscription: subscription,
        userPlatform: platform,

        // Browser information
        browser: browserInfo.browser,
        platform: browserInfo.platform,
        language: browserInfo.language,
        timezone: browserInfo.timezone,
        screenResolution: browserInfo.screenResolution,
        viewportSize: browserInfo.viewportSize,
        cookieEnabled: browserInfo.cookieEnabled,
        onlineStatus: browserInfo.onLine,
      };

      const errorReport = `Error Report:
Timestamp: ${errorInfo.timestamp}
URL: ${errorInfo.url}

Error Details:
Error: ${errorInfo.error}
Error Boundary: ${errorInfo.errorBoundary}
Sentry Event ID: ${errorInfo.sentryEventId}

User Information:
Logged In: ${errorInfo.userLoggedIn}
User ID: ${errorInfo.userId}
Subscription: ${errorInfo.userSubscription}
Platform: ${errorInfo.userPlatform}

Browser Information:
Browser: ${errorInfo.browser}
Platform: ${errorInfo.platform}
Screen Resolution: ${errorInfo.screenResolution}
Viewport Size: ${errorInfo.viewportSize}
Cookies Enabled: ${errorInfo.cookieEnabled}

Please include this information when creating a Discord ticket.`;

      await navigator.clipboard.writeText(errorReport);
      setCopySuccess(true);
    } catch (err) {
      console.error("Failed to copy error report:", err);
    }
  };

  return (
    <SentryErrorBoundary
      beforeCapture={(scope) => {
        const nav = navigator as any;
        scope.setContext("browser", {
          userAgent: navigator.userAgent,
          // Prefer UA-CH when available; falls back gracefully
          brand: nav.userAgentData?.brands
            ?.map((b: any) => `${b.brand} ${b.version}`)
            .join(", "),
          platform: navigator.platform,
          language: navigator.language,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          screen: `${screen.width}x${screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        scope.setContext("user", {
          loggedIn: Boolean(user?.uuid),
          id: user?.uuid ?? null,
          subscription: user?.subscription ?? null,
          platform: user?.platform ?? null,
        });
      }}
      fallback={({ error, componentStack, eventId }) => {
        // Capture error details for the copy function
        if (error && !errorDetails.error) {
          setErrorDetails({
            error: error as Error,
            componentStack,
            eventId,
          });
        }

        return (
          <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center bg-gray-900 text-white">
            <h1 className="text-2xl font-bold mb-4">
              {t("errorBoundary.title")}
            </h1>
            <p className="mb-4">
              We're sorry - something's gone wrong on our end.
            </p>

            <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
              <div className="flex flex-row justify-center  sm:flex-row gap-4 mb-8">
                <button
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors touch-device:w-full"
                  onClick={() => {
                    window.location.href = "/";
                  }}
                >
                  {t("errorBoundary.backToHome")}
                </button>
                <button
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors touch-device:w-full"
                  onClick={() => window.location.reload()}
                >
                  {t("errorBoundary.refreshPage")}
                </button>
              </div>
              <p className="font-medium text-dark-secondary">
                {t("errorBoundary.helpWithEmoji")}
              </p>
              <p className="text-sm text-dark-tertiary mb-3">
                {t("errorBoundary.copyPrompt")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
                <button
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center gap-2"
                  onClick={copyErrorReport}
                >
                  {copySuccess ? (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>{t("copied")}</span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      <span>{t("errorBoundary.copyReport")}</span>
                    </>
                  )}
                </button>
                <a
                  href={DISCORD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                  </svg>
                  {t("errorBoundary.createTicket")}
                </a>
              </div>
            </div>
          </div>
        );
      }}
    >
      {children}
    </SentryErrorBoundary>
  );
};
